import { ChevronRight } from "lucide-react"
import { useMatches,Link } from "@tanstack/react-router"
import {
Breadcrumb,
BreadcrumbItem,
BreadcrumbLink,
BreadcrumbList,
BreadcrumbPage,
BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import React from "react"

// Top-level routes that live in the sidebar's "Sales" group but aren't
// URL-nested under /sales — the crumb trail shows the group prefix anyway
// (non-link, like the Inventory group). Segments whose sidebar label differs
// from the URL are retitled: /sales → "Orders", /audit → "Activity".
const SALES_GROUP_SEGMENTS = new Set(['sales', 'shifts', 'audit', 'customers'])
const SALES_GROUP_RETITLES: Record<string, string> = { sales: 'Orders', audit: 'Activity' }

// A selected entity lives in the URL as its uuid, which is meaningless as a
// crumb ("Ed3bd0c1 746a 400e b316 2dd52abf2e13"). Pages that know the entity's
// name pass it as `entityLabel`; until it loads the crumb is dropped rather
// than showing the raw id.
const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

interface PageBreadcrumbProps {
  /** Extra non-URL crumb appended after the route segments — e.g. the
   *  currently selected entity ("FC-PBJ6A3") on pages where selection is
   *  component state rather than a route param. */
  trailing?: string | null
  /** Display name for a uuid route segment (e.g. the selected brand's name).
   *  Only applies to segments that are actually uuids. */
  entityLabel?: string | null
}

export function PageBreadcrumb({ trailing, entityLabel }: PageBreadcrumbProps = {}) {
  const matches = useMatches()

  const breadcrumbs = React.useMemo(() => {
    const paths: { title: string; href: string; isNoLink: boolean }[] = []

    const activeMatch = matches[matches.length - 1]
    if (!activeMatch) return paths

    const pathname = activeMatch.pathname
    const segments = pathname.split('/').filter(Boolean)

    let currentPath = ''
    segments.forEach((segment: string) => {
      currentPath += `/${segment}`

      if (UUID_SEGMENT.test(segment)) {
        // Never title-case a uuid — that is what produced
        // "Ed3bd0c1 746a 400e b316 2dd52abf2e13".
        if (entityLabel) {
          paths.push({ title: entityLabel, href: currentPath, isNoLink: true })
        }
        return
      }

      const title = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')
      // Disable the link for grouping segments like Inventory
      const isNoLink = title === "Inventory"
      paths.push({ title, href: currentPath, isNoLink })
    })

    if (segments[0] && SALES_GROUP_SEGMENTS.has(segments[0])) {
      const retitle = SALES_GROUP_RETITLES[segments[0]]
      if (retitle && paths[0]) {
        paths[0] = { ...paths[0], title: retitle }
      }
      paths.unshift({ title: 'Sales', href: '/sales', isNoLink: true })
    }

    if (trailing) {
      paths.push({ title: trailing, href: `${currentPath}#`, isNoLink: true })
    }

    return paths
  }, [matches, trailing, entityLabel])

  if (breadcrumbs.length === 0) return null

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1
          const shouldLink = !isLast && !item.isNoLink

          return (
            // href alone is not unique: on /sales the "Sales" group crumb and
            // the retitled "Orders" crumb share href="/sales".
            <React.Fragment key={`${index}-${item.href}`}>
              <BreadcrumbItem>
                {!shouldLink ? (
                  <BreadcrumbPage className={!isLast ? "text-muted-foreground/80 font-normal" : ""}>
                    {item.title}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={item.href}>{item.title}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && (
                <BreadcrumbSeparator>
                  <ChevronRight className="h-4 w-4" />
                </BreadcrumbSeparator>
              )}
            </React.Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}